import { useCallback, useEffect, useRef, useState } from "react";

// Keyframe engine for the service icons, ported from the Figr prototype (figr/motion.tsx).
// Colours are raw hex because they go straight into SVG attributes.
export const SEA = "#F1E6DF";
export const VELVET = "#7F2A3C";
export const PAPRIKA = "#CE4D42";

export const STROKE = {
  stroke: SEA,
  strokeWidth: 2.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  fill: "none",
};

const cubic = (x1, y1, x2, y2) => {
  const f = (a, b, s) => 3 * a * (1 - s) * (1 - s) * s + 3 * b * (1 - s) * s * s + s * s * s;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let lo = 0;
    let hi = 1;
    let s = x;
    for (let i = 0; i < 28; i++) {
      s = (lo + hi) / 2;
      if (f(x1, x2, s) < x) lo = s;
      else hi = s;
    }
    return f(y1, y2, s);
  };
};

const EASES = {
  linear: (x) => x,
  out: cubic(0.2, 0.7, 0.2, 1),
  pop: cubic(0.2, 0.7, 0.2, 1.4), // ~12% overshoot
};

// keys: [progress 0–1, value, easing into this key]
export function track(keys, t) {
  if (t <= keys[0][0]) return keys[0][1];
  for (let i = 1; i < keys.length; i++) {
    const [a, va] = keys[i - 1];
    const [b, vb, ease = "out"] = keys[i];
    if (t <= b) {
      const u = b === a ? 1 : (t - a) / (b - a);
      return va + (vb - va) * EASES[ease](u);
    }
  }
  return keys[keys.length - 1][1];
}

export const r2 = (n) => Math.round(n * 100) / 100;

// Stroke draw-on in real path units.
export function draw(length, p) {
  if (p >= 0.999) return {};
  if (p <= 0.001) return { opacity: 0 };
  return { strokeDasharray: `${r2(length)} ${r2(length)}`, strokeDashoffset: r2(length * (1 - p)) };
}

export const tx = (x, y = 0) =>
  Math.abs(x) < 0.005 && Math.abs(y) < 0.005 ? undefined : `translate(${r2(x)} ${r2(y)})`;

export const scaleAt = (cx, cy, s) =>
  Math.abs(s - 1) < 0.002 ? undefined : `translate(${cx} ${cy}) scale(${r2(s)}) translate(${-cx} ${-cy})`;

export function mix(a, b, f) {
  if (f >= 0.999) return b;
  if (f <= 0.001) return a;
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return "#" + pa.map((v, i) => Math.round(v + (pb[i] - v) * f).toString(16).padStart(2, "0")).join("");
}

const prefersReducedMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Plays t from 0 to 1 over `duration` ms. With reduced motion it always shows the final frame.
export function usePlayer(duration, initial = 1) {
  const [reduced] = useState(prefersReducedMotion);
  const [t, setT] = useState(initial);
  const frame = useRef(0);
  const playing = useRef(false);

  const play = useCallback(
    (force = false) => {
      if (reduced) return;
      if (playing.current && !force) return;
      cancelAnimationFrame(frame.current);
      playing.current = true;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / duration);
        setT(p);
        if (p < 1) frame.current = requestAnimationFrame(step);
        else playing.current = false;
      };
      frame.current = requestAnimationFrame(step);
    },
    [duration, reduced]
  );
  const finish = useCallback(() => {
    cancelAnimationFrame(frame.current);
    playing.current = false;
    setT(1);
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);
  return { t: reduced ? 1 : t, play, finish };
}
